import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../config";

// Social media post tracker (db/sritaru-posts.sql). Bhaskar, 2026-10-24: track every post,
// its topic, the names used and whose letters were published, so nothing is missed or repeated.
// Rows come from password-gated functions. Archive is soft and reversible.

const STATUSES = ["published", "scheduled", "draft", "retired"];
const STATUS_STYLES = {
  published: "bg-green-100 text-green-700",
  scheduled: "bg-blue-100 text-blue-700",
  draft: "bg-yellow-100 text-yellow-700",
  retired: "bg-gray-200 text-gray-600",
};
const LINK_LABELS = {
  instagram: "Instagram",
  facebook_page: "Facebook Page",
  youtube: "YouTube",
  personal_facebook: "Personal FB",
};
const EMPTY = {
  post_date: "", status: "published", title: "", format: "", language: "",
  instagram: "", facebook_page: "", youtube: "", personal_facebook: "", whatsapp: "",
  people: "", letter_ids: "", caption: "", notes: "",
};

const toForm = (p) => ({
  ...EMPTY,
  post_id: p.post_id,
  post_date: p.post_date || "",
  status: p.status,
  title: p.title || "",
  format: p.format || "",
  language: p.language || "",
  instagram: p.links?.instagram || "",
  facebook_page: p.links?.facebook_page || "",
  youtube: p.links?.youtube || "",
  personal_facebook: p.personal_facebook || "",
  whatsapp: p.whatsapp || "",
  people: (p.people || []).join("\n"),
  letter_ids: (p.letter_ids || []).join("\n"),
  caption: p.caption || "",
  notes: p.notes || "",
});

const splitLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function PostForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm";
  const label = "block text-xs font-semibold text-gray-600 mb-1";
  const save = () => {
    if (!f.title.trim()) return alert("Please give the post a title / topic.");
    const links = {};
    ["instagram", "facebook_page", "youtube"].forEach((k) => f[k].trim() && (links[k] = f[k].trim()));
    onSave({
      ...(f.post_id ? { post_id: f.post_id } : {}),
      post_date: f.post_date || null,
      status: f.status,
      title: f.title.trim(),
      format: f.format.trim() || null,
      language: f.language.trim() || null,
      links,
      personal_facebook: f.personal_facebook.trim() || null,
      whatsapp: f.whatsapp.trim() || null,
      people: splitLines(f.people),
      letter_ids: splitLines(f.letter_ids),
      caption: f.caption.trim() || null,
      notes: f.notes.trim() || null,
    });
  };
  return (
    <div className="bg-white border border-sritaru-200 rounded-xl p-4 mb-4 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><label className={label}>Date</label><input type="date" className={input} value={f.post_date} onChange={set("post_date")} /></div>
        <div>
          <label className={label}>Status</label>
          <select className={input} value={f.status} onChange={set("status")}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label className={label}>Format</label><input className={input} value={f.format} onChange={set("format")} placeholder="reel, letter reel, email…" /></div>
        <div><label className={label}>Language</label><input className={input} value={f.language} onChange={set("language")} placeholder="English / Telugu" /></div>
        <div className="md:col-span-4"><label className={label}>Title / topic</label><input className={input} value={f.title} onChange={set("title")} /></div>
        <div><label className={label}>Instagram link</label><input className={input} value={f.instagram} onChange={set("instagram")} /></div>
        <div><label className={label}>Facebook Page link</label><input className={input} value={f.facebook_page} onChange={set("facebook_page")} /></div>
        <div><label className={label}>YouTube link</label><input className={input} value={f.youtube} onChange={set("youtube")} /></div>
        <div><label className={label}>Personal Facebook</label><input className={input} value={f.personal_facebook} onChange={set("personal_facebook")} placeholder="shared? who was tagged" /></div>
        <div className="md:col-span-2"><label className={label}>WhatsApp (Status, groups, people)</label><textarea rows={2} className={input} value={f.whatsapp} onChange={set("whatsapp")} /></div>
        <div><label className={label}>Names used (one per line)</label><textarea rows={2} className={input} value={f.people} onChange={set("people")} /></div>
        <div><label className={label}>Letter ids (one per line)</label><textarea rows={2} className={input} value={f.letter_ids} onChange={set("letter_ids")} /></div>
        <div className="md:col-span-2"><label className={label}>Caption</label><textarea rows={2} className={input} value={f.caption} onChange={set("caption")} /></div>
        <div className="md:col-span-2"><label className={label}>Notes</label><textarea rows={2} className={input} value={f.notes} onChange={set("notes")} /></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={save} className="px-4 py-2 rounded-lg bg-sritaru-700 text-white text-sm font-semibold">Save</button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function AdminPosts({ password }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("all"); // all | published | scheduled | draft | retired | archived
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | post_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("sritaru_admin_posts_list", {
        p_password: password,
        p_archived: view === "archived",
      });
      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      alert("Error loading posts: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [password, view]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (post) => {
    const { error } = await supabase.rpc("sritaru_admin_post_save", { p_password: password, p_post: post });
    if (error) return alert("Error saving: " + error.message);
    setEditing(null);
    await load();
  };

  const archive = async (id, yes) => {
    if (yes && !window.confirm("Archive this post? You can restore it from the Archived view.")) return;
    const { error } = await supabase.rpc("sritaru_admin_post_archive", { p_password: password, p_post_id: id, p_archive: yes });
    if (error) return alert("Error: " + error.message);
    await load();
  };

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => view === "all" || view === "archived" || r.status === view)
      .filter((r) => !needle || [r.title, r.format, r.language, r.notes, r.caption, r.whatsapp, r.personal_facebook,
        ...(r.people || []), ...(r.letter_ids || []), r.anveshi_id].join(" ").toLowerCase().includes(needle));
  }, [rows, view, q]);

  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, rows.filter((r) => r.status === s).length])), [rows]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", ...STATUSES, "archived"].map((v) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${view === v ? "bg-sritaru-700 text-white" : "bg-white text-sritaru-700 border border-sritaru-200"}`}>
            {v[0].toUpperCase() + v.slice(1)}{counts[v] !== undefined && view !== "archived" ? ` (${counts[v]})` : ""}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, names, letters, notes…"
          className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
        <button onClick={() => setEditing("new")} className="px-4 py-1.5 rounded-lg bg-sritaru-700 text-white text-sm font-semibold">+ Add post</button>
      </div>

      {editing === "new" && <PostForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} />}
      {loading && <p className="text-gray-500 text-sm">Loading…</p>}
      {!loading && shown.length === 0 && <p className="text-gray-500 text-sm">No posts here.</p>}

      <div className="space-y-3">
        {shown.map((r) =>
          editing === r.post_id ? (
            <PostForm key={r.post_id} initial={toForm(r)} onSave={save} onCancel={() => setEditing(null)} />
          ) : (
            <div key={r.post_id} className="bg-white border border-sritaru-100 rounded-xl p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-500 w-24">{r.post_date || "no date"}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                {r.format && <span className="text-xs text-gray-500">{r.format}</span>}
                {r.language && <span className="text-xs text-gray-500">· {r.language}</span>}
                <span className="ml-auto flex gap-2">
                  <button onClick={() => setEditing(r.post_id)} className="text-xs text-sritaru-700 underline">Edit</button>
                  {view === "archived"
                    ? <button onClick={() => archive(r.post_id, false)} className="text-xs text-sritaru-700 underline">Restore</button>
                    : <button onClick={() => archive(r.post_id, true)} className="text-xs text-gray-500 underline">Archive</button>}
                </span>
              </div>
              <p className="font-semibold text-sritaru-800">{r.title}</p>
              <div className="flex flex-wrap gap-3 text-sm mt-1">
                {Object.entries(r.links || {}).map(([k, url]) => (
                  <a key={k} href={url} target="_blank" rel="noopener noreferrer" className="text-sritaru-600 underline">{LINK_LABELS[k] || k}</a>
                ))}
              </div>
              {(r.people || []).length > 0 && (
                <p className="text-sm text-gray-700 mt-1"><span className="font-semibold">Names:</span> {r.people.join(" · ")}</p>
              )}
              {(r.letter_ids || []).length > 0 && (
                <p className="text-xs text-gray-500 mt-1">Letters: {r.letter_ids.map((id) => id.slice(0, 8)).join(", ")}</p>
              )}
              {r.personal_facebook && <p className="text-sm text-gray-700 mt-1"><span className="font-semibold">Personal FB:</span> {r.personal_facebook}</p>}
              {r.whatsapp && <p className="text-sm text-gray-700 mt-1"><span className="font-semibold">WhatsApp:</span> {r.whatsapp}</p>}
              {r.notes && <p className="text-sm text-gray-500 mt-1">{r.notes}</p>}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default AdminPosts;
